import { useState, useEffect, useCallback } from 'react';
import { assignmentService } from '../services/assignmentService';

export const useAssignments = (params = {}) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchAssignments = useCallback(async (fetchParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Students should fetch assignments (not just submissions) to see all available assignments
      // getMySubmissions is only for viewing submission history
      const response = await assignmentService.getAssignments({
        ...params,
        ...fetchParams
      });
      
      // Handle response structure
      setAssignments(response.data.data.assignments || []);
      if (response.data.data.pagination) {
        setPagination(response.data.data.pagination);
      }
    } catch (err) {
      // Handle rate limiting specifically
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and refresh the page.');
        console.warn('Rate limited, stopping further requests');
        return; // Don't retry on rate limit
      }
      setError(err.response?.data?.message || 'Failed to fetch assignments');
      console.error('Error fetching assignments:', err);
    } finally {
      setLoading(false);
    }
  }, [params.scope, params.limit, params.page, params.classId, params.subjectId, params.teacherId, params.status, params.type, params.search, params.sortBy, params.sortOrder, params.academicYear, params.term]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const createAssignment = async (data) => {
    try {
      const response = await assignmentService.createAssignment(data);
      await fetchAssignments(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create assignment';
    }
  };

  const updateAssignment = async (id, data) => {
    try {
      const response = await assignmentService.updateAssignment(id, data);
      await fetchAssignments(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update assignment';
    }
  };

  const deleteAssignment = async (id) => {
    try {
      await assignmentService.deleteAssignment(id);
      await fetchAssignments(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete assignment';
    }
  };

  const submitAssignment = async (id, data) => {
    try {
      const response = await assignmentService.submitAssignment(id, data);
      await fetchAssignments(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to submit assignment';
    }
  };

  const gradeSubmission = async (assignmentId, submissionId, data) => {
    try {
      const response = await assignmentService.gradeSubmission(assignmentId, submissionId, data);
      await fetchAssignments(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to grade submission';
    }
  };

  const refreshAssignments = () => {
    fetchAssignments();
  };

  const loadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchAssignments({ page: pagination.page + 1 });
    }
  };

  return {
    assignments,
    loading,
    error,
    pagination,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    submitAssignment,
    gradeSubmission,
    refreshAssignments,
    loadMore,
    fetchAssignments
  };
};

export const useAssignment = (id) => {
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAssignment = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await assignmentService.getAssignment(id);
      setAssignment(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch assignment');
      console.error('Error fetching assignment:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  return {
    assignment,
    loading,
    error,
    refreshAssignment: fetchAssignment
  };
};

export const useAssignmentSubmissions = (assignmentId, params = {}) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    if (!assignmentId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await assignmentService.getSubmissions(assignmentId, params);
      setSubmissions(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  }, [assignmentId, params]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  return {
    submissions,
    loading,
    error,
    refreshSubmissions: fetchSubmissions
  };
};
